import intlTelInput from "intl-tel-input/dist/js/intlTelInput.mjs";
import de from "intl-tel-input/dist/js/locale/de.js";
import en from "intl-tel-input/dist/js/locale/en.js";
import fr from "intl-tel-input/dist/js/locale/fr.js";
import pl from "intl-tel-input/dist/js/locale/pl.js";
import uk from "intl-tel-input/dist/js/locale/uk.js";
import { getCurrentLanguage } from "../shared/language";

const UTILS_URL = "https://cdn.jsdelivr.net/npm/intl-tel-input@29.1.2/dist/js/utils.js";
const DEFAULT_COUNTRIES = {
  de: "de",
  en: "ua",
  fr: "fr",
  pl: "pl",
  uk: "ua",
};
const UI_TRANSLATIONS = { de, en, fr, pl, uk };
const phoneControllers = new WeakMap();

function getLocaleCountry(supportedCountries) {
  const navigatorLanguages = navigator.languages || [navigator.language];

  for (const locale of navigatorLanguages) {
    const region = locale?.match(/[-_]([a-z]{2})\b/i)?.[1]?.toLowerCase();
    if (region && supportedCountries.has(region)) return region;
  }

  return DEFAULT_COUNTRIES[getCurrentLanguage()] || DEFAULT_COUNTRIES.en;
}

async function getInitialCountry() {
  const supportedCountries = new Set(intlTelInput.getAllCountries().map(({ iso2 }) => iso2));
  const geoCountry = window.__GEO__?.country?.toLowerCase();
  if (geoCountry && supportedCountries.has(geoCountry)) return geoCountry;

  try {
    const response = await fetch("/cdn-cgi/trace", {
      cache: "no-store",
      credentials: "same-origin",
    });
    const country = response.ok
      ? (await response.text()).match(/^loc=([a-z]{2})$/im)?.[1]?.toLowerCase()
      : "";
    if (country && supportedCountries.has(country)) return country;
  } catch {}

  return getLocaleCountry(supportedCountries);
}

function getCountryNameLocale() {
  return document.documentElement.lang?.trim() || getCurrentLanguage();
}

function getUiTranslations() {
  return UI_TRANSLATIONS[getCurrentLanguage()] || UI_TRANSLATIONS.en;
}

function ensureErrorMessage(input) {
  const field = input.closest(".elem__form-field");
  if (!field || field.querySelector(".input__error-msg")) return;

  const errorMessage = document.createElement("div");
  const errorMessageText = document.createElement("div");

  errorMessage.className = "input__error-msg";
  errorMessage.hidden = true;
  errorMessage.setAttribute("aria-hidden", "true");
  errorMessageText.className = "input__error-msg-txt";
  errorMessage.append(errorMessageText);
  field.append(errorMessage);
}

function ensureHiddenInput(form, name, dataKey) {
  const existingInput = form.querySelector(`input[type="hidden"][data-phone-hidden="${dataKey}"]`);
  if (existingInput) return existingInput;

  const hiddenInput = document.createElement("input");
  hiddenInput.type = "hidden";
  hiddenInput.name = name;
  hiddenInput.dataset.phoneHidden = dataKey;
  form.append(hiddenInput);
  return hiddenInput;
}

function ensureCountryContainerLines(input) {
  const countryContainer = input
    .closest(".modern-phone-input")
    ?.querySelector(".iti__country-container");
  if (!countryContainer || countryContainer.querySelector(":scope > .elem__line-bottom")) return;

  countryContainer.insertAdjacentHTML(
    "beforeend",
    `
        <div aria-hidden="true" data-wf--line-bottom--variant="light" class="elem__line-bottom">
            <div class="elem__line-bottom__right"></div>
            <div class="elem__line-bottom__left"></div>
            <div class="elem__line-bottom__bottom"></div>
        </div>
        <div data-wf--line-top--variant="light" class="elem__line-top">
            <div class="elem__line-top__right"></div>
            <div class="elem__line-top__left"></div>
        </div>
    `
  );
}

function movePhoneLabelsIntoWrapper(input) {
  const wrapper = input.closest(".modern-phone-input");
  const field = wrapper?.parentElement;
  if (!field?.classList.contains("elem__form-field")) return;

  field.querySelectorAll(":scope > .elem__form-label").forEach((label) => {
    wrapper.insertBefore(label, input);
  });
}

function splitPhoneHelpBlocks(input) {
  const phoneWrapper = input.closest(".iti.modern-phone-input");
  const inputField = phoneWrapper?.parentElement;
  const phoneHelpField = inputField?.closest(
    ".phone-help.is-phone, .elem__form-field--phone-help.is-phone"
  );
  if (!phoneWrapper || !inputField?.classList.contains("elem__form-field") || !phoneHelpField) {
    return false;
  }

  phoneWrapper.before(input);

  const manualWrapper = inputField.parentElement;
  if (
    manualWrapper !== phoneHelpField &&
    manualWrapper?.matches(".modern-phone-input, .elem__form-input-flag--dropdown") &&
    manualWrapper.children.length === 1 &&
    manualWrapper.firstElementChild === inputField
  ) {
    phoneHelpField.insertBefore(inputField, manualWrapper);
    manualWrapper.remove();
  }

  phoneHelpField.insertBefore(phoneWrapper, inputField);
  input.style.removeProperty("padding-left");
  input.addEventListener("countrychange", () => input.style.removeProperty("padding-left"));
  return true;
}

function getFallbackNumber(input, selectedCountry) {
  const inputValue = input.value.trim();
  if (!inputValue) return "";

  const digits = inputValue.replace(/\D/g, "");
  if (inputValue.startsWith("+")) return `+${digits}`;
  if (!selectedCountry?.dialCode) return digits;
  return `+${selectedCountry.dialCode}${digits}`;
}

export function syncPhoneInput(input) {
  const controller = phoneControllers.get(input);
  if (!controller) return;

  const selectedCountry = controller.instance.getSelectedCountry();
  let fullNumber = "";

  if (input.value.trim()) {
    try {
      fullNumber = controller.instance.getNumber();
    } catch (error) {
      fullNumber = getFallbackNumber(input, selectedCountry);
    }
  }

  controller.fullNumberInput.value = fullNumber;
  controller.countryInput.value = input.value.trim() ? selectedCountry?.iso2 || "" : "";
}

export function getPhoneInputValidity(input) {
  if (!input.value.trim()) return true;

  const controller = phoneControllers.get(input);
  if (!controller) return null;

  if (!controller.isReady) {
    if (!controller.loadFailed) return null;
    return /^\+\d{7,15}$/.test(controller.fullNumberInput.value);
  }

  return controller.instance.isValidNumber() === true;
}

function waitForUtilsBeforeSubmit(form, input, controller) {
  form.addEventListener(
    "submit",
    async (event) => {
      if (controller.isResubmitting || controller.isReady || controller.loadFailed) return;
      if (!input.value.trim()) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const submitter = event.submitter;
      try {
        await controller.instance.promise;
      } catch (error) {
        controller.loadFailed = true;
      }

      if (!form.isConnected) return;

      controller.isResubmitting = true;
      form.requestSubmit(submitter instanceof HTMLElement ? submitter : undefined);
      controller.isResubmitting = false;
    },
    true
  );
}

function initPhoneInput(input) {
  if (phoneControllers.has(input)) return;

  const form = input.closest("form");
  if (!form) return;

  ensureErrorMessage(input);

  const baseName = input.name || "Phone";
  const fullNumberInput = ensureHiddenInput(
    form,
    input.dataset.phoneFullName || `${baseName}-Full`,
    "full-number"
  );
  const countryInput = ensureHiddenInput(
    form,
    input.dataset.phoneCountryName || `${baseName}-Country`,
    "country"
  );
  const instance = intlTelInput(input, {
    containerClass: "modern-phone-input",
    countryNameLocale: getCountryNameLocale(),
    dropdownParent: document.body,
    initialCountryLookup: getInitialCountry,
    loadUtils: () => import(UTILS_URL),
    searchInputClass: "modern-phone-search",
    separateDialCode: true,
    strictMode: true,
    uiTranslations: getUiTranslations(),
  });
  ensureCountryContainerLines(input);
  if (!splitPhoneHelpBlocks(input)) movePhoneLabelsIntoWrapper(input);

  const controller = {
    countryInput,
    fullNumberInput,
    instance,
    isReady: false,
    isResubmitting: false,
    loadFailed: false,
  };

  phoneControllers.set(input, controller);
  input.dataset.phoneInputInit = "true";

  input.addEventListener("input", () => syncPhoneInput(input));
  input.addEventListener("countrychange", () => syncPhoneInput(input));
  waitForUtilsBeforeSubmit(form, input, controller);

  instance.promise
    .then(() => {
      controller.isReady = true;
      syncPhoneInput(input);

      if (input.closest(".elem__form-field")?.classList.contains("is-touched")) {
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }
    })
    .catch(() => {
      controller.loadFailed = true;
      syncPhoneInput(input);
    });

  syncPhoneInput(input);
}

function init() {
  document.querySelectorAll(".elem__form-input[type='tel']").forEach(initPhoneInput);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
